import {
  getDate,
  formatTimestamp,
} from 'components/Inputs/DateTimeInput/helpers';
import styled from '@emotion/styled';
import { VARIABLE_DATA_TYPES } from 'ts-components/inputs/ValueInput/types';
import { head } from 'utility/snippets';

import { Step } from 'pages/AutomationEngine/types/automation';

import { TFunction, useTranslation } from 'react-i18next';

import StepCard from '__pages/AutomationEngine/Engine/Steps/StepCard';
import {
  VariableDetails,
  VariableSource,
} from '__pages/AutomationEngine/dialogs/VariableWizard/types';
import { HistoryVariableValue } from '__pages/AutomationEngine/HistoryFrameWork';
import { GenericOption } from 'pages/SmartConnectors/types';
import {
  VariableNumberFormatTypes,
  getBooleanString,
  getFormatString,
  getVariableFormatMetaData,
  getNumberString,
} from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/format-meta-data';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const Container = styled.span`
  font-size: 1.4em;
  * {
    font-size: 1em;
  }
`;

export const content = ({ step }: { step: Step }) =>
  step.history?.[0]?.detailedLog ? (
    <HistoryVariableValue detailedLog={step.history[0].detailedLog} />
  ) : (
    <Container dangerouslySetInnerHTML={{ __html: step.description }} />
  );

type GetDescription = {
  step: VariableSource;
  t: TFunction;
};

type GetDescriptionValue = {
  details: VariableDetails;
  t: TFunction;
};

const EMPTY_STRING = '<i>empty</i>';

// const getNumberString = (
//   num: string,
//   formatString: VariableNumberFormatTypes = 'include_comma'
// ) => {
//   const formated = Number(num.replaceAll(',', ''));

//   return isNaN(formated)
//     ? num
//     : formatString === 'include_comma'
//       ? formated.toLocaleString()
//       : formated.toString();
// };

const wrapWithQuotes = (str?: string | null) =>
  str ? (str === EMPTY_STRING ? str : `‘${str}’`) : '';

const getDescriptionValue = ({ details, t }: GetDescriptionValue) => {
  let descriptionValue: string = t('Unknown');

  let descriptionPlural = '';

  const { variable } = details;
  const outputMetaData = getVariableFormatMetaData(variable.dataType);

  if (details?.sources?.length) {
    const firstSource = head(details.sources);

    switch (firstSource?.sourceType) {
      case 'static': {
        const { staticVariable } = firstSource;

        descriptionValue = staticVariable
          ? typeof staticVariable?.value === 'object'
            ? staticVariable?.value?.label
            : staticVariable?.value || ''
          : '';

        if (
          descriptionValue &&
          variable.dataType === VARIABLE_DATA_TYPES.datetime
        ) {
          try {
            const date = new Date(descriptionValue);
            descriptionValue = wrapWithQuotes(
              format(
                date,
                getFormatString(outputMetaData!, variable.outputFormat!)!
              )
            );
          } catch (error) {
            descriptionValue = wrapWithQuotes(
              formatTimestamp(descriptionValue)
            );
          }
        } else if (
          descriptionValue &&
          variable.dataType === VARIABLE_DATA_TYPES.date
        ) {
          try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            descriptionValue = wrapWithQuotes(
              formatInTimeZone(
                descriptionValue,
                timeZone,
                getFormatString(outputMetaData!, variable.outputFormat!)!
              )
            );
          } catch (error) {
            descriptionValue = wrapWithQuotes(getDate(descriptionValue));
          }
        } else if (
          descriptionValue &&
          variable.dataType === VARIABLE_DATA_TYPES.boolean
        ) {
          try {
            const booleanString = getBooleanString(
              outputMetaData!,
              variable.outputFormat!,
              descriptionValue === 'True'
            );

            descriptionValue = wrapWithQuotes(booleanString);
          } catch (error) {
            descriptionValue = wrapWithQuotes(
              descriptionValue === 'True' ? t('Yes') : t('No')
            );
          }
        } else if (
          descriptionValue &&
          variable.dataType === VARIABLE_DATA_TYPES.number
        ) {
          descriptionValue = wrapWithQuotes(
            getNumberString(
              descriptionValue,
              variable.outputFormat! as VariableNumberFormatTypes
            )
          );
        } else {
          descriptionValue = wrapWithQuotes(descriptionValue || EMPTY_STRING);
        }
        break;
      }
      case 'logged_activity_trigger': {
        const { loggedActivityVariable } = firstSource;

        switch (loggedActivityVariable?.sourceSubtype) {
          case 'logged_by':
            descriptionValue = t('Activity Logged By Employee');
            break;

          case 'logged_at':
            descriptionValue = t('Activity Logged Time');
            break;

          case 'due_datetime':
            descriptionValue = t('Logged Activity Due Date');
            break;

          case 'activity_field_value':
            descriptionValue = t('activity field {{label}}', {
              label: wrapWithQuotes(
                (loggedActivityVariable?.value as GenericOption)?.label
              ),
            });
            break;

          case 'record_association':
            descriptionValue = t('Activity Association {{label}}', {
              label: wrapWithQuotes(
                (loggedActivityVariable?.value as GenericOption)?.label
              ),
            });
            break;

          default:
            break;
        }
        break;
      }
      case 'activity_past_due_trigger': {
        const { activityPassedDueVariable } = firstSource;

        switch (activityPassedDueVariable?.sourceSubtype) {
          case 'scheduled_at':
            descriptionValue = t('Scheduled At');
            break;

          case 'assignee':
            descriptionValue = t('Activity Assignee');
            break;

          case 'due_datetime':
            descriptionValue = t('Activity Due Date');
            break;

          case 'record_association':
            descriptionValue = t('Activity Association {{label}}', {
              label: wrapWithQuotes(
                (activityPassedDueVariable?.value as GenericOption)?.label
              ),
            });
            break;

          default:
            break;
        }
        break;
      }
      case 'field_value': {
        const { fieldVariable } = firstSource;
        descriptionValue = wrapWithQuotes(fieldVariable?.field?.label);
        break;
      }

      default:
        break;
    }

    // add the plural
    if (details.sources?.length > 1) {
      descriptionPlural = t(', and {{N}} more', {
        N: details.sources.length! - 1,
      });
    }
  }
  return { descriptionValue, descriptionPlural };
};

export const getDescription = ({ step, t }: GetDescription) => {
  const { details } = step;
  const { descriptionValue, descriptionPlural } = getDescriptionValue({
    details,
    t,
  });
  return t(`‘{{variableName}}’ = {{descriptionValue}}{{descriptionPlural}}`, {
    variableName: details.variable.name,
    descriptionValue,
    descriptionPlural,
  });
};

type CardProps = {
  type: 'initialize_variable';
  step: Step;
  title: string;
};

export default function VariableInitializationCard({
  step,
  title,
  ...others
}: CardProps) {
  const { t } = useTranslation();
  return (
    <StepCard {...others} title={step.history ? t('Variable') : title}>
      {content({ step })}
    </StepCard>
  );
}
